import { formatPhone } from './phone';
import { isPrivateAccount } from './accountType';

// The rules for showing people (guards, supervisors, clients, district managers), in one place.
// The server builds full names the same way (App\People::fullName in Laravel).

// "First Last": each part trimmed, joined with a single space; either part may be missing.
export function fullName(first, last) {
	return [first, last]
		.map((part) => (part == null ? '' : String(part).replace(/\s+/g, ' ').trim()))
		.filter((part) => part !== '')
		.join(' ');
}

// The full name of a record as the API sends it. kind is 'guard', 'supervisor', 'client' or
// 'manager' (a district manager, a users row): guards and supervisors have firstname / secondname,
// clients and district managers have name / sname.
export function personName(record, kind) {
	if (!record) {
		return '';
	}
	if (kind === 'client' || kind === 'manager') {
		return fullName(record.name, record.sname);
	}
	return fullName(record.firstname, record.secondname);
}

// The name of an admin-website login (a users row): a district manager (level d) or a reseller (level b) is
// a person and gets the full name; a company or admin keeps its single name.
export function userName(user) {
	if (!user) {
		return '';
	}
	return user.level === 'd' || user.level === 'b' ? fullName(user.name, user.sname) : fullName(user.name);
}

// The CID rule (guards, supervisors, clients): digits only, 4 to 10 of them. The server checks the same.
export const CID_MESSAGE = 'The CID must be 4 to 10 digits';

export function isValidCid(cid) {
	return /^\d{4,10}$/.test(cid == null ? '' : String(cid).trim());
}

// For a CID input: keeps only the digits of what was typed.
export function digitsOnly(text) {
	return text == null ? '' : String(text).replace(/\D/g, '');
}

// True for the server's "this phone number is also someone else's" reply (code 409).
export function isSharedPhoneReply(res) {
	return !!(res && res.data && res.data.code === 409 && res.data.phone_shared);
}

// "The phone number +44 7960 251744"
function thePhoneNumber(phone) {
	return phone ? 'The phone number ' + formatPhone(phone) : 'This phone number';
}

// "Test01 Guard01 (guard)"
function nameAndRole(person) {
	return (person.name || 'someone without a name') + ' (' + person.role + ')';
}

// "Test01 Guard01 (guard) as their 2nd number" (the server says which of their numbers it is)
function savedOn(person) {
	return nameAndRole(person) + ' as their ' + (person.number_label || 'number');
}

// The people on each number: [[phone, [person, ...]], ...] in the server's order, which has each number's
// first owner (who has had it longest, first_owner) first
function byPhone(usedBy) {
	const groups = [];
	usedBy.forEach((person) => {
		const group = groups.find((g) => g[0] === (person.phone || ''));
		if (group) {
			group[1].push(person);
		} else {
			groups.push([person.phone || '', [person]]);
		}
	});
	groups.forEach((group) => {
		const first = group[1].findIndex((person) => person.first_owner);
		if (first > 0) {
			group[1].unshift(group[1].splice(first, 1)[0]);
		}
	});
	return groups;
}

// Who a number is saved on, its first owner first: "Jo Bloggs (guard) as their main number, who has had it
// longest, and also used by Ann Lee (client) as their 2nd number and Al Day (supervisor) as their main number"
function savedOnAll(people) {
	return savedOn(people[0])
		+ (people.length > 1 ? ', who has had it longest, and also used by ' + people.slice(1).map(savedOn).join(' and ') : '');
}

// The plain shared-phone warning, one sentence per number (the same as App\People::sharedPhoneMessage):
// "The phone number +44 7960 251744 is also saved on Jo Bloggs (guard) as their main number. Save it for
// this guard too?"
export function sharedPhoneQuestion(res, kind) {
	const usedBy = (res && res.data && res.data.used_by) || [];
	const ask = ' Save it for this ' + (kind || 'person') + ' too?';
	if (!usedBy.length) {
		return 'This phone number is also saved on someone else in ' + (isPrivateAccount() ? 'your account.' : 'the company.') + ask;
	}
	return byPhone(usedBy)
		.map(([phone, people]) => thePhoneNumber(phone) + ' is also saved on ' + savedOnAll(people) + '.')
		.join(' ') + ask;
}

// One CID per person (App\People in Laravel): a guard, supervisor and client with the same phone and CID
// are one person. When the phone number belongs to someone of another kind, the admin is asked whether the
// person on the form ($kind: 'guard', 'supervisor' or 'client'; req is the save, with id on an update) and
// that one are the same person (about the first such match); otherwise the plain warning.
// Returns the form's sharedPhone state: { message, details, req, person } (person null for the warning;
// details: the lines under the question saying what each answer does).
export function sharedPhoneState(res, req, kind) {
	const usedBy = (res && res.data && res.data.used_by) || [];
	const person = usedBy.find((match) => match.same_kind === false) || null;
	if (!person) {
		return { message: sharedPhoneQuestion(res, kind), details: [], req: req, person: null };
	}
	const role = kind || 'person';
	// "Jo Smith (the supervisor you are editing)", or with no name typed yet "the supervisor you are adding"
	const doing = 'the ' + role + ' you are ' + (req && req.id ? 'editing' : 'adding');
	const name = personName(req, kind === 'client' ? 'client' : 'guard');
	const self = name ? name + ' (' + doing + ')' : doing;
	// Everyone on that number, its first owner first (who may be of this kind, not the one asked about)
	const onNumber = byPhone(usedBy).find((group) => group[0] === (person.phone || ''))[1];
	const message = thePhoneNumber(person.phone) + ' is already saved on ' + savedOnAll(onNumber) + '. '
		+ 'Are ' + self + ' and ' + nameAndRole(person) + ' the same person?';
	const details = [
		'Yes: this ' + role + ' gets the same CID as that ' + person.role + ' (' + (person.cid || 'no CID') + ').',
		'No: they are two different people sharing this phone number.',
	];
	return { message: message, details: details, req: req, person: person };
}

// The save sent again after the question. With samePerson (Yes, the same person) the server saves the
// record with that person's CID; without it (No, different people, or the plain warning's Yes) the usual
// CID rule applies.
export function confirmedSharedPhoneRequest(req, samePerson) {
	const extra = { confirm_shared_phone: true };
	if (samePerson) {
		extra.same_person = samePerson.kind + ':' + samePerson.id;
		extra.CID = samePerson.cid;
	}
	return Object.assign({}, req, extra);
}
